import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminNotifications } from "@/mocks/admin";
import type { AdminNotification } from "@/mocks/admin";

const typeConfig: Record<AdminNotification["type"], { icon: string; bgClass: string }> = {
  order_placed: { icon: "ri-shopping-cart-2-line", bgClass: "bg-secondary-100 text-secondary-700" },
  order_shipped: { icon: "ri-truck-line", bgClass: "bg-primary-100 text-primary-700" },
  order_delivered: { icon: "ri-checkbox-circle-line", bgClass: "bg-accent-100 text-accent-700" },
  low_stock: { icon: "ri-alert-line", bgClass: "bg-primary-100 text-primary-700" },
};

export default function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AdminNotification[]>(adminNotifications);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, handleClickOutside]);

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleNotificationClick = (notif: AdminNotification) => {
    setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
    setOpen(false);
    navigate(notif.linkTo);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-foreground-500 hover:text-foreground-900 hover:bg-background-200/70 transition-colors cursor-pointer"
      >
        <i className="ri-notification-3-line text-lg"></i>
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold flex items-center justify-center leading-none">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-background-50 rounded-xl border border-background-200/70 shadow-lg shadow-foreground-950/5 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 font-heading">Notifications</h4>
              <p className="text-[11px] text-foreground-400 mt-0.5">{unreadCount} unread</p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <i className="ri-notification-off-line text-3xl text-foreground-300"></i>
                <p className="text-sm text-foreground-400 mt-2">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const config = typeConfig[notif.type];
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full text-left px-5 py-3.5 flex items-start gap-3 transition-colors cursor-pointer border-b border-background-100 last:border-b-0 hover:bg-background-100/60 ${
                      !notif.read ? "bg-background-100/40" : ""
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${config.bgClass}`}>
                      <i className={`${config.icon} text-sm`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground-900 truncate">{notif.title}</p>
                        {!notif.read && (
                          <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{notif.description}</p>
                      <p className="text-[10px] text-foreground-400 mt-1.5">{notif.time}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-background-200/70 bg-background-100/50">
            <a
              href="/admin/orders"
              className="text-xs font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
            >
              View all orders
            </a>
          </div>
        </div>
      )}
    </div>
  );
}